/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 */

import invariant from 'tiny-invariant'

import { logger } from '../logger'

export const REVIVE_CONTENTS_FIRST = Symbol('REVIVE_CONTENTS_FIRST')

type Constructable<RawData = any> = new (data: RawData) => unknown

class TypeRegistry {
  add<TypeName extends string, Type extends Constructable, TypesByName extends object>(
    this: TypesByName,
    typeName: TypeName,
    Type: Type
  ): asserts this is TypesByName & { [Key in TypeName]: Type } {
    invariant(
      // @ts-expect-error
      !(typeName in this) || this[typeName] === Type,
      `Cannot call typeRegistry.add("${typeName}", ...) more than once`
    )
    if ({}.hasOwnProperty.call(Type.prototype, '__typename')) {
      invariant(
        Type.prototype.__typename === typeName,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `typeRegistry.add("${typeName}", ...): This type was already registered using the name ${Type.prototype.__typename}`
      )
    }
    Object.defineProperty(Type.prototype, '__typename', { value: typeName })
    // @ts-expect-error
    this[typeName] = Type
  }
}

export const typeRegistry: TypeRegistry = new TypeRegistry()

export const reviveBizos = <T extends object>(preloadedState: T | null | undefined) => {
  if (!preloadedState) {
    return
  }
  for (const [key, value] of Object.entries(preloadedState) as Array<[keyof T, unknown]>) {
    if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        let i = 0
        for (const item of value) {
          if (item && typeof item === 'object') {
            const bizo = reviveBizo(item)
            if (bizo) {
              value[i] = bizo
            } else {
              reviveBizos(item)
            }
          }
          i++
        }
        continue
      }
      const bizo = reviveBizo(value)
      if (bizo) {
        preloadedState[key] = bizo
        continue
      }
      reviveBizos(value)
    }
  }
}

const reviveBizo = (value: object) => {
  if ('__typename' in value && typeof value.__typename === 'string') {
    if (value.__typename in typeRegistry && !(value.__typename in TypeRegistry.prototype)) {
      const Type =
        typeRegistry[
          value.__typename as Exclude<
            keyof typeof typeRegistry,
            keyof typeof TypeRegistry.prototype
          >
        ]
      if (value instanceof Type) {
        return value
      }
      if (REVIVE_CONTENTS_FIRST in Type && Type[REVIVE_CONTENTS_FIRST]) {
        reviveBizos(value)
      }
      // @ts-expect-error
      return new Type(value)
    }
    logger.warn('Unrecognized __typename', value.__typename)
  }
}
